import * as ko from 'knockout';
import fetch from 'node-fetch';

import { LogoutResponse, User, App } from '../contracts';
import template from './User.html';
import { LogonmeConfiguration, ViewId } from '../configuration';
import { AuthenticatedLocalizationData, AuthenticatedLocalizationManager } from '../localization';

class Home {

  lm: AuthenticatedLocalizationManager | undefined;
  apps: ko.ObservableArray<App> = ko.observableArray<App>([]);

  title: ko.Observable<string> = ko.observable<string>("");
  subTitle: ko.Observable<string> = ko.observable<string>("");
  link1Text: ko.Observable<string> = ko.observable<string>("");
  link1Uri: ko.Observable<string> = ko.observable<string>("");
  link2Text: ko.Observable<string> = ko.observable<string>("");
  link2Uri: ko.Observable<string> = ko.observable<string>("");
  link3Text: ko.Observable<string> = ko.observable<string>("");
  link3Uri: ko.Observable<string> = ko.observable<string>("");

  link4Text: ko.Observable<string> = ko.observable<string>("");
  link4Uri: ko.Observable<string> = ko.observable<string>("");
  link5Text: ko.Observable<string> = ko.observable<string>("");
  link5Uri: ko.Observable<string> = ko.observable<string>("");

  displayName: ko.Observable<string> = ko.observable<string>("");
  description: ko.Observable<string> = ko.observable<string>("");
  userName: ko.Observable<string> = ko.observable<string>("");
  userId: ko.Observable<string> = ko.observable<string>("");
  emailAddress: ko.Observable<string> = ko.observable<string>("");
  mobilePhone: ko.Observable<string> = ko.observable<string>("");

  authenticatedWith: ko.Observable<string> = ko.observable<string>("");

  logoutButton: ko.Observable<string> = ko.observable<string>("");

  // Computed properties - initialized in constructor
  selectedLanguage: ko.PureComputed<string>;
  activateView: ko.PureComputed<boolean>;

  constructor() {
    // Initialize computed properties
    this.selectedLanguage = ko.pureComputed<string>(() => global.Logonme.SelectedLanguage());
    this.activateView = ko.pureComputed<boolean>(() => global.Logonme.ActiveView() === ViewId.Home);

    this.selectedLanguage.subscribe((lang: string) => {
      this.updateStrings(lang);
    });

    this.activateView.subscribe((activate: boolean) => {
      if (activate) {
        global.Logonme.log("Home:activate");
        this.initialize();
      }
    });
  }

  private async initialize(): Promise<void> {
    global.Logonme.log("Home:initialize");
    await this.getLocalizationModel(global.Logonme.Configuration);
    await this.doGetCurrentUser();
    this.updateStrings(this.selectedLanguage());
  }

  private updateStrings(lang: string): void {
    this.title(global.Logonme.LocalizationManager.getText("user-title", lang, ""));
    this.subTitle(global.Logonme.LocalizationManager.getText("user-subtitle", lang, ""));

    this.link1Uri(global.Logonme.LocalizationManager.getText("user-link1-src", lang, ""));
    this.link1Text(global.Logonme.LocalizationManager.getText("user-link1-text", lang, ""));
    this.link2Uri(global.Logonme.LocalizationManager.getText("user-link2-src", lang, ""));
    this.link2Text(global.Logonme.LocalizationManager.getText("user-link2-text", lang, ""));
    this.link3Uri(global.Logonme.LocalizationManager.getText("user-link3-src", lang, ""));
    this.link3Text(global.Logonme.LocalizationManager.getText("user-link3-text", lang, ""));
    // footer
    this.link4Uri(global.Logonme.LocalizationManager.getText("user-link4-src", lang, ""));
    this.link4Text(global.Logonme.LocalizationManager.getText("user-link4-text", lang, ""));
    this.link5Uri(global.Logonme.LocalizationManager.getText("user-link5-src", lang, ""));
    this.link5Text(global.Logonme.LocalizationManager.getText("user-link5-text", lang, ""));

    this.logoutButton(global.Logonme.LocalizationManager.getText("logout-button", lang));
  }

  private async getCurrentUser(): Promise<User> {
    const uri = `${global.Logonme.Configuration.baseUrl}/ls/${global.Logonme.Configuration.tenant}/whoami`;
    const response = await fetch(uri);
    const userResponse = await response.json<User>();
    return userResponse;
  }

  private async getApplications(): Promise<App[]> {
    const uri = `${global.Logonme.Configuration.baseUrl}/ls/${global.Logonme.Configuration.tenant}/applications/my`;
    const response = await fetch(uri);
    const applicationsResponse = await response.json<App[]>();
    return applicationsResponse;
  }

  async doSelectApplication(app: App): Promise<void> {
    const lang = this.selectedLanguage();
    const linkUri = this.lm?.getAppProperty(app, "linkUri", "about:blank", lang, "default");
    window.open(linkUri, '_blank');
  }

  private async getLocalizationModel(config: LogonmeConfiguration): Promise<void> {
    const uri = `${config.baseUrl}/ls/${config.tenant}/applications/i18n`;
    const response = await fetch(uri);
    const localizationModel = await response.json<AuthenticatedLocalizationData>();
    this.lm = new AuthenticatedLocalizationManager(localizationModel);
  }

  private async logout(): Promise<LogoutResponse> {
    const uri = `${global.Logonme.Configuration.baseUrl}/ls/${global.Logonme.Configuration.tenant}/forms/logout`;
    const response = await fetch(uri);
    const logoutResponse = await response.json<LogoutResponse>();
    return logoutResponse;
  }

  async doLogout(): Promise<void> {
    await this.logout().then((response: LogoutResponse) => {
      global.Logonme.AuthState(response.authState);
      global.Logonme.SelectedMethod(null);
    });
  }

  async doGetCurrentUser(): Promise<void> {
    global.Logonme.log("doGetCurrentUser");
    const apps = await this.getApplications();

    if (global.Logonme.FrontendConfig.autoRedirectSingleApplication && apps.length === 1) {
      const lang = this.selectedLanguage();
      const linkUri = this.lm?.getAppProperty(apps[0], "linkUri", null, lang, "default");
      if (linkUri) {
        global.Logonme.log(`auto-redirect: single application, redirecting to ${linkUri}`);
        window.location.href = linkUri;
        return;
      }
    }

    this.apps(apps);

    const user = await this.getCurrentUser();
    this.displayName(user.displayName);
    this.description(user.description);
    this.userName(user.username);
    this.userId(user.userId);
    this.emailAddress(user.emailAddress);
    this.mobilePhone(user.mobilePhone);

    this.authenticatedWith(user.authenticatedWith);
  }
}

export default { viewModel: Home, template: template };
